import axiosInstance from './axios';
import { requestHeader } from '../store/actions/sharedUtils';
import {
  OrderVersionDto,
  AnchorPlanFile,
  OrderFileDto,
  CoverOptionsResponse,
  HardwareReplacementCandidatesResult,
  OrderRecalcPreviewRequest,
  OrderRecalcPreviewResult,
} from '../interfaces/OrderDetail';

interface OrderDescriptionResultDTO {
  sN: number;
  fileName: string;
  sono: string;
  code: string;
  description: string;
  link: string;
  blobFile: boolean;
}

function unwrapArray<T>(raw: any): T[] { return Array.isArray(raw) ? raw : raw?.data ?? []; }

export const getOrderVersions = async (soNo: string): Promise<OrderVersionDto[]> => {
  const response = await axiosInstance.get(`/OrderDetails/${soNo}/versions`, {
    headers: requestHeader.headers,
  });
  return unwrapArray<OrderVersionDto>(response.data);
};

export const getOrderDescription = async (soNo: string): Promise<OrderDescriptionResultDTO[]> => {
  const response = await axiosInstance.get(`/Orders/order-description/${soNo}`, {
    headers: requestHeader.headers,
  });
  return unwrapArray<OrderDescriptionResultDTO>(response.data);
};

export const appendOrderNote = async (soNo: string, note: string, versionLabel?: string): Promise<void> => {
  await axiosInstance.patch(`/Orders/${soNo}/notes`, { note, versionLabel }, {
    headers: requestHeader.headers,
  });
};

export const getCoverOptions = async (soNo: string): Promise<CoverOptionsResponse> => {
  const response = await axiosInstance.get(`/Orders/${soNo}/cover-options`, {
    headers: requestHeader.headers,
  });
  return response.data?.data ?? response.data;
};

export const getHardwareReplacementCandidates = async (
  soNo: string,
  lineIdcol: number
): Promise<HardwareReplacementCandidatesResult> => {
  const response = await axiosInstance.get(`/Orders/${soNo}/hardware-lines/${lineIdcol}/replacement-candidates`, {
    headers: requestHeader.headers,
  });
  return response.data?.data ?? response.data;
};

export const swapHardwareLine = async (
  soNo: string,
  lineIdcol: number,
  newItem: string,
  versionLabel?: string,
  quantity?: number
): Promise<void> => {
  await axiosInstance.patch(
    `/Orders/${soNo}/hardware-lines/${lineIdcol}`,
    { newItem, versionLabel, quantity },
    { headers: requestHeader.headers }
  );
};

export const updateCover = async (
  soNo: string,
  colorId: number,
  materialId: number,
  colorDescrip: string,
  materialDescrip: string,
  versionLabel?: string
): Promise<void> => {
  await axiosInstance.patch(
    `/Orders/${soNo}/cover`,
    { colorId, materialId, colorDescrip, materialDescrip, versionLabel },
    { headers: requestHeader.headers }
  );
};

export const previewOrderRecalc = async (
  soNo: string,
  request: OrderRecalcPreviewRequest
): Promise<OrderRecalcPreviewResult> => {
  const response = await axiosInstance.post(
    `/Orders/${soNo}/recalc-preview`,
    request,
    { headers: requestHeader.headers }
  );
  return response.data?.data ?? response.data;
};

export interface UpdateOrderDetailsDto {
  poNumber?: string;
  shipVia?: string;
  fob?: string;
  poolOwnerFirstName?: string;
  poolOwnerLastName?: string;
  poolOwnerAddress?: string;
  poolOwnerCity?: string;
  poolOwnerState?: string;
  poolOwnerZip?: string;
  shippingName?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingZip?: string;
}

export const updateOrderDetails = async (
  soNo: string,
  dto: UpdateOrderDetailsDto
): Promise<void> => {
  await axiosInstance.patch(
    `/Orders/${soNo}/details`,
    dto,
    { headers: requestHeader.headers }
  );
};

export const extractAnchorPlan = (descriptions: OrderDescriptionResultDTO[]): AnchorPlanFile | null => {
  const anchor = descriptions.find((d) => d.description?.toUpperCase().includes('ANCHOR') || d.description?.toUpperCase().includes('AB PLOT'));
  if (!anchor) return null;
  return { fileName: anchor.fileName, link: anchor.link, blobFile: anchor.blobFile };
};

export const stampOrderChangelog = (soNo: string, changes: string[] = []) =>
  axiosInstance.post(`/Orders/${soNo}/changelog`, { changes }, { headers: requestHeader.headers });

export const createOrder = (soNo: string, selectedVersion: string) =>
  axiosInstance.post(`/Orders/${soNo}/create-order`, { selectedVersion }, { headers: requestHeader.headers });
